import type { ReactNode } from 'react';
import { Card, CardHeader } from './card';
import { Chip } from './chip';

export interface TabbedTableCardTab {
  value: string;
  label: string;
  /** A count badge after the label, shown only while the tab is active. */
  count?: number | null;
}

/**
 * A card with folder tabs in its header, optional filters at the header's right and a body — the
 * frame of the list pages (SA orders and customers, compliance monitor, vehicle-service-offers).
 * Ported from portal-ui-lib's `tabbed-table-card`, drawn as the legacy Bootstrap `.nav-tabs`
 * inside a `.card-header`; the caller owns the active tab.
 */
export function TabbedTableCard({
  tabs,
  activeTab,
  onTabChange,
  label,
  filters,
  flush = false,
  children,
}: {
  tabs: readonly TabbedTableCardTab[];
  activeTab: string;
  onTabChange: (value: string) => void;
  /** Accessible name of the tab list. */
  label?: string;
  filters?: ReactNode;
  /** No padding round the body, for a table that runs to the card's edges. */
  flush?: boolean;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader
        className="items-end pb-0"
        actions={filters ? <div className="pb-3">{filters}</div> : undefined}
      >
        <div role="tablist" aria-label={label} className="-mb-px flex flex-wrap">
          {tabs.map(tab => {
            const active = tab.value === activeTab;
            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onTabChange(tab.value)}
                className={`inline-flex items-center gap-2 rounded-t-bs border px-4 py-2 text-sm focus:z-10 ${
                  active
                    ? 'border-line border-b-white bg-white text-ink'
                    : 'border-transparent text-ink-soft hover:border-b-line hover:text-brand'
                }`}
              >
                {tab.label}
                {active && tab.count !== undefined && tab.count !== null ? (
                  <Chip pill variant="primary">
                    {tab.count}
                  </Chip>
                ) : null}
              </button>
            );
          })}
        </div>
      </CardHeader>
      <div role="tabpanel" className={flush ? '' : 'p-5'}>
        {children}
      </div>
    </Card>
  );
}
